/**
 * Marcas visuales del checkout: la del COMERCIO y la de cada MEDIO DE PAGO.
 *
 * <p>Las dos siguen la misma regla: si hay un logo configurado se usa ese, y si no se dibuja un
 * monograma. El monograma no es un relleno perezoso — es lo que evita que un comercio recien dado de
 * alta, o una pasarela cuyo logo todavia no se ha cargado, dejen un hueco gris en una pantalla de
 * pago. El color se deriva del propio nombre, asi que es estable entre cargas y distinto entre
 * medios sin que nadie tenga que configurarlo.
 *
 * <p>Los logos de marca de las pasarelas se sirven desde el propio checkout (ver
 * `gatewayAssets.ts`), tomados de los paquetes oficiales para integradores y sin alterarlos. Lo que
 * configure el tenant en `public_config.logo_url` manda siempre sobre ellos.
 */
import type { JSX } from 'react';

/**
 * URLs de logo admitidas: HTTPS absoluto —igual que el logo del tenant (plan 7.3.5), para que un
 * `http:` configurado por error no rompa el candado del navegador— y rutas absolutas del PROPIO
 * checkout (`/gateway-webpay.webp`), que son los activos que servimos nosotros.
 */
export function httpsUrlOrNull(candidate: string | undefined | null): string | null {
  if (candidate === undefined || candidate === null || candidate === '') {
    return null;
  }
  // Ruta propia: mismo origen, sin esquema que validar. `//` queda fuera a proposito: es una URL
  // protocol-relative hacia otro host disfrazada de ruta.
  if (candidate.startsWith('/') && !candidate.startsWith('//')) {
    return candidate;
  }
  try {
    return new URL(candidate).protocol === 'https:' ? candidate : null;
  } catch {
    return null;
  }
}

/** Fondo y texto de un monograma. Viajan en pareja: sueltos se pueden combinar tonos ilegibles. */
interface MonogramColor {
  bg: string;
  fg: string;
}

/**
 * Paleta sobria para los monogramas. Son tonos de fondo suaves con texto oscuro del mismo tono:
 * legibles, y sin competir con el color primario del tenant, que es el que manda en los botones.
 *
 * <p>El tipo es una TUPLA NO VACIA, no un array normal, y eso es intencionado: con
 * `noUncheckedIndexedAccess` indexar un array da siempre `MonogramColor | undefined`, incluso en la
 * posicion 0, asi que el codigo tenia que asegurar a mano que la paleta no estaba vacia. La tupla
 * pone esa garantia en el tipo —hay al menos un color— y entonces `PALETTE[0]` ya es un
 * `MonogramColor` para el compilador. Anadir o quitar tonos aqui no cambia nada mientras quede uno;
 * si alguien la deja vacia, el error salta en esta linea y no en quien la usa.
 */
const PALETTE: readonly [MonogramColor, ...MonogramColor[]] = [
  { bg: '#e0f2fe', fg: '#075985' },
  { bg: '#dcfce7', fg: '#166534' },
  { bg: '#fef3c7', fg: '#92400e' },
  { bg: '#ede9fe', fg: '#5b21b6' },
  { bg: '#ffe4e6', fg: '#9f1239' },
  { bg: '#ccfbf1', fg: '#115e59' },
];

/**
 * Suma estable de los codigos: el mismo nombre da siempre el mismo color.
 *
 * <p>`total` nunca es negativo, asi que el resto siempre cae dentro de la paleta. El `??` cubre lo
 * unico que el compilador no puede demostrar al indexar por `number` —que el indice esta en rango—
 * y por eso cae al primer color, que la tupla garantiza que existe.
 */
function paletteFor(seed: string): MonogramColor {
  let total = 0;
  for (let index = 0; index < seed.length; index += 1) {
    total = (total + seed.charCodeAt(index) * (index + 1)) % 1_000_003;
  }
  return PALETTE[total % PALETTE.length] ?? PALETTE[0];
}

/** Hasta dos iniciales: «ApiPay Demo Merchant» -> «AD», «Webpay Plus» -> «WP». */
function initialsOf(name: string): string {
  const words = name
    .split(/[\s—–-]+/)
    .map((word) => word.trim())
    .filter((word) => word.length > 0);
  if (words.length === 0) {
    return '?';
  }
  const first = words[0]?.charAt(0) ?? '';
  const second = words.length > 1 ? (words[1]?.charAt(0) ?? '') : '';
  return (first + second).toUpperCase();
}

export interface BrandMarkProps {
  /** Nombre del comercio o del medio de pago; de aqui salen iniciales y color. */
  name: string;
  /** Logo configurado. Si no es HTTPS se ignora y se cae al monograma. */
  logoUrl?: string | undefined;
  /** `circle` para el comercio, `square` para un medio de pago. */
  shape?: 'circle' | 'square';
  /** Lado del cuadro, en px. */
  size?: number;
}

/**
 * Marca de una entidad. El `alt` va vacio y `aria-hidden`: el nombre siempre se pinta al lado como
 * texto, asi que repetirlo aqui solo anadiria ruido a un lector de pantalla.
 */
export function BrandMark({
  name,
  logoUrl,
  shape = 'square',
  size = 40,
}: BrandMarkProps): JSX.Element {
  const src = httpsUrlOrNull(logoUrl);
  const radius = shape === 'circle' ? '9999px' : '10px';

  if (src !== null) {
    // Se fija el ALTO y el ancho corre libre hasta un tope: un logotipo de marca es apaisado
    // —el de Webpay Plus mide 285x72— y encajarlo en un cuadrado lo dejaria diminuto y con aire
    // muerto a los lados. Los manuales de marca prohiben deformarlos, asi que no se recorta.
    return (
      <img
        src={src}
        alt=""
        aria-hidden="true"
        referrerPolicy="no-referrer"
        loading="lazy"
        style={{ height: size, maxWidth: size * 3, borderRadius: shape === 'circle' ? radius : 0 }}
        className="w-auto shrink-0 object-contain"
      />
    );
  }

  const { bg, fg } = paletteFor(name);
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        backgroundColor: bg,
        color: fg,
        fontSize: Math.round(size * 0.38),
      }}
      className="inline-flex shrink-0 select-none items-center justify-center font-semibold tracking-tight"
    >
      {initialsOf(name)}
    </span>
  );
}
